/* eslint-disable @typescript-eslint/unbound-method */
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, forwardRef, Input, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormControl, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator, ValidatorFn, Validators } from '@angular/forms';
import { SelectItem } from 'primeng/api';
import { AutoComplete } from 'primeng/autocomplete';
import { Subject, Subscription } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { AppConfig } from 'src/app/app-config.loader';
import { AnagraficaClassificheService } from 'src/app/core/anagrafica-classifiche.service';
import { AnagraficaIndirizziService } from 'src/app/core/anagrafica-indirizzi.service';
import { AnagraficaPrecedenzeService } from 'src/app/core/anagrafica-precedenze.service';
import { AnagraficaQualificheService } from 'src/app/core/anagrafica-qualifiche.service';
import { AnagraficaSezioniService } from 'src/app/core/anagrafica-sezione.service';
import { AnagraficaStatiService } from 'src/app/core/anagrafica-stati.service';
import { AnagraficheService } from 'src/app/core/anagrafiche.service';
import { DocumentoNormativaService } from 'src/app/core/documento-normativa.service';
import { HierarchyService } from 'src/app/core/hierarchy.service';
import { ProdottiSoftwareService } from 'src/app/core/prodotti-software.service';
import { Classifica } from '../../models/classifica.model';
import { Documento } from '../../models/documento.model';
import { DocumentoDestinatario } from '../../models/documentoDestinatario.model';
import { DocumentoRiproduzioneRow } from '../../models/documentoRiproduzione.model';
import { Indirizzo } from '../../models/indirizzo.model';
import { Posizione } from '../../models/posizione.model';
import { Qualifica } from '../../models/qualifica.model';
import { Sezione } from '../../models/sezione.model';
import { StoricoStatoDocumento } from '../../models/storicoStatoDocumento.model';
import { SoggettoDialogComponent } from '../soggetto-dialog/soggetto-dialog.component';
import { CaricoArrivo, CaricoPartenza, DatiDocumentoForm } from './dati-documento.model';

@Component({
  selector: 'app-dati-documento',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: 'dati-documento.component.html',
  styleUrls: ['dati-documento.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatiDocumentoComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => DatiDocumentoComponent),
      multi: true
    }
  ]
})

export class DatiDocumentoComponent implements OnInit, OnDestroy, ControlValueAccessor, Validator {
  @Input() posizione: (Posizione | undefined)[] | null = null;
  @Input() trasmissioneRiproduzione = false;
  @Input() documentoRiproduzione: DocumentoRiproduzioneRow | undefined;
  @Input() destinatariArray: Indirizzo[] = [];

  @ViewChild('classificaInput') classificaInput!: AutoComplete;
  @ViewChild(SoggettoDialogComponent) private soggettoDialog?: SoggettoDialogComponent;

  public documento = {} as Documento;
  public storicoStati: StoricoStatoDocumento[] | undefined = [];
  public hierarchy: string[];
  public hierarchyDescriptionIndex: number;
  public edit = true;
  public newDocument = true;
  public showPrecedenza: boolean;
  public docForm: FormGroup<DatiDocumentoForm>;


  public lingue: SelectItem[];
  public modalitaFormazione: SelectItem[];
  public tipiDocumento: SelectItem[];
  public ambitiUtilizzo: SelectItem[];
  public prodottiSoftware: SelectItem[] = [];
  public caveatDiSicurezza: SelectItem[];
  public soggetti: Indirizzo[] = [];

  public AnagraficaClassificheService = AnagraficaClassificheService;
  public AnagraficaQualificheService = AnagraficaQualificheService;
  public AnagraficaIndirizziService = AnagraficaIndirizziService;
  public AnagraficaPrecedenzeService = AnagraficaPrecedenzeService;
  public AnagraficaSezioniService = AnagraficaSezioniService;
  public AnagraficaStatiService = AnagraficaStatiService;

  public dataDocumentoSignal = signal<Date | null>(null);
  private documentoAnalogicoSignal = signal<boolean>(false);
  private classificaSignal = signal<Classifica | null>(null);
  private caveatDiSicurezzaSignal = signal<number | null>(null);

  public isNuovaNormativa = computed(() => {
    const dataDoc = this.dataDocumentoSignal();
    return this.documentoNormativaService.isUnderNewNormativa(dataDoc);
  });

  private onChangeFn!: (_: Documento) => void;
  private onTouchedFn!: () => void;
  private docFormChangesSubscription: Subscription;

  private notifier$ = new Subject<void>();

  constructor(
    public anagraficheService: AnagraficheService,
    private hierarchyService: HierarchyService,
    private fb: FormBuilder,
    private cdRef: ChangeDetectorRef,
    private appConfig: AppConfig,
    private documentoNormativaService: DocumentoNormativaService,
    private prodottiSoftwareService: ProdottiSoftwareService,
  ) {
    this.showPrecedenza = this.appConfig.settings.showPrecedenza;
    this.hierarchy = this.hierarchyService.getHierarchyLabels();
    this.hierarchyDescriptionIndex = this.hierarchyService.getDescriptionIndex();
    this.lingue = [
      { label: 'Italiano', value: 'Italiano' },
      { label: 'Inglese', value: 'Inglese' },
      { label: 'Francese', value: 'Francese' },
      { label: 'Ita/Eng', value: 'Ita/Eng' },
      { label: 'Ita/Fra', value: 'Ita/Fra' }
    ];

    this.modalitaFormazione = [
      { label: 'A: mediante l\'utilizzo degli appositi strumenti software disponibili nell\'ambito degli Organi di Sicurezza', value: 1 },
      { label: 'B: mediante acquisizione per via telematica o supporto informatico', value: 2 },
      { label: 'C: acquisizione della copia per immagini su supporto informatico di un documento analogico classificato', value: 3 },
      { label: 'D: mediante registrazione informatica delle informazioni risultanti da transazioni o processi informatici o dalla presentazione telematica di dati attraverso moduli o formulari resi disponibili all\'utente', value: 4 },
      { label: 'E: mediante generazione o raggruppamento, anche in via automatica, di un insieme di dati o registrazioni, provenienti da una o più basi dati, anche appartenenti a più soggetti interoperanti, secondo una struttura logica predeterminata e memorizzata in forma statica', value: 5 }
    ];

    this.tipiDocumento = [
      { label: 'Lettera', value: 1 },
      { label: 'Documento', value: 2 },
      { label: 'Nota per atti', value: 3 },
      { label: 'Fattura', value: 4 },
      { label: 'Contratto', value: 5 },
      { label: 'Decreto', value: 6 },
      { label: 'Attestato', value: 7 },
      { label: 'Certificato', value: 8 },
      { label: 'Registro', value: 9 },
      { label: 'Verbale', value: 10 }
    ];

    this.ambitiUtilizzo = [
      { label: 'Amministrativo', value: 1 },
      { label: 'Informativo', value: 2 },
      { label: 'Operativo', value: 3 },
      { label: 'Sicurezza', value: 4 }
    ];

    this.caveatDiSicurezza = [
      { label: 'A diffusione esclusiva', value: 1 },
      { label: 'Informazioni non classificate controllate', value: 2 },
      { label: 'Esclusivo per il titolare', value: 3 },
      { label: 'Altro', value: 4 }
    ];

    const defaultQualifica = this.anagraficheService.qualifiche.getAsArray().find(q => q.qualificaId === 1);
    this.docForm = this.fb.group<DatiDocumentoForm>({
      oggetto: this.fb.control<string | null>(null, [Validators.required, Validators.maxLength(255)]),
      dataDocumento: this.fb.control<Date | null>(null, Validators.required),
      classifica: this.fb.control<Classifica | null>(null, Validators.required),
      qualifica: this.fb.control<Qualifica | null | undefined>(defaultQualifica, Validators.required),
      sezioneId: this.fb.control<number | null>(null, Validators.required),
      esercitazione: this.fb.control<boolean | null>(false),
      mittente: this.fb.control<Indirizzo | null>(null),
      ambito: this.fb.control<string | null>(null, Validators.maxLength(50)),
      protocolloMittente: this.fb.control<string | null>(null, Validators.maxLength(255)),
      destinatari: this.fb.control<Indirizzo[] | null>(null),
      precedenzaId: this.fb.control<number | null>(null),
      dataInvio: this.fb.control<Date | null>(null),
      note: this.fb.control<string | null>(null, Validators.maxLength(3000)),
      paroleChiave: this.fb.control<string[]>([], control => control.value && control.value.some(keyword => keyword.length > 255) ? { maxLength: true } : null),
      lingua: this.fb.control<string | null>(null, Validators.maxLength(50)),
      documentoAnalogico: this.fb.nonNullable.control<boolean>(false),
      prodottoSoftwareId: this.fb.control<number | null>(null),
      numeroPagine: this.fb.control<number | null>(null),
      organoSicurezzaOriginatore: this.fb.control<string | null>(null, Validators.maxLength(255)),
      organoSicurezzaDestinatario: this.fb.control<string | null>(null, Validators.maxLength(255)),
      caveatDiSicurezzaId: this.fb.control<number | null>(null),
      caveatDiSicurezzaAltro: this.fb.control<string | null>(null, Validators.maxLength(255)),
      modalitaFormazioneId: this.fb.control<number | null>(null),
      tipoDocumentoId: this.fb.control<number | null>(null),
      ambitoUtilizzoId: this.fb.control<number | null>(null),
      numeroControllo: this.fb.control<string | null>(null, [Validators.required, Validators.maxLength(30)]),
      noCarico: this.fb.nonNullable.control<boolean>(false),
      scaricaAllegati: this.fb.nonNullable.control<boolean>(false),
      caricoArrivo: this.fb.group<CaricoArrivo>({
        numeroEsemplariRicevuti: this.fb.control<string | null>(null, [Validators.required, Validators.maxLength(50)]),
        numeroDistintoCopie: this.fb.control<string | null>(null, [Validators.required, Validators.maxLength(50)]),
        allegatiRicevuti: this.fb.control<string | null>(null, [Validators.required, Validators.maxLength(50)]),
        copieInCarico: this.fb.control<string | null>(null, [Validators.required, Validators.maxLength(50)])
      }),
      caricoPartenza: this.fb.group<CaricoPartenza>({
        allegatiOriginati: this.fb.control<string | null>(null, Validators.required)
      }),
      noteEtichetta: this.fb.control<string | null>(null, Validators.maxLength(50))
    });

    this.docFormChangesSubscription = this.docForm.valueChanges.subscribe(() => this.changed());
    this.documentoAnalogicoSignal.set(this.docForm.controls.documentoAnalogico.value);
    this.classificaSignal.set(this.docForm.controls.classifica.value);
    this.caveatDiSicurezzaSignal.set(this.docForm.controls.caveatDiSicurezzaId.value);
    this.docForm.controls.documentoAnalogico.valueChanges
      .pipe(takeUntil(this.notifier$))
      .subscribe(value => {
        this.documentoAnalogicoSignal.set(value);
      });

    this.docForm.controls.classifica.valueChanges
      .pipe(takeUntil(this.notifier$))
      .subscribe(value => {
        this.classificaSignal.set(value);
      });

    this.docForm.controls.caveatDiSicurezzaId.valueChanges
      .pipe(takeUntil(this.notifier$))
      .subscribe(value => {
        this.caveatDiSicurezzaSignal.set(value);
        if (value !== 4) {
          this.docForm.controls.caveatDiSicurezzaAltro.setValue(null);
        }
      });

    effect(() => {
      const isNuova = this.isNuovaNormativa();
      const modalitaControl = this.docForm.controls.modalitaFormazioneId;
      const tipoDocumentoControl = this.docForm.controls.tipoDocumentoId;
      const ambitoUtilizzoControl = this.docForm.controls.ambitoUtilizzoId;
      const mittenteControl = this.docForm.controls.mittente;
      const prodottoSoftwareControl = this.docForm.controls.prodottoSoftwareId;
      const documentoAnalogico = this.documentoAnalogicoSignal();
      const classificaValue = this.classificaSignal();
      const hasClassificaConVisibilita = !!classificaValue && !this.isClassificaNonClassificata(classificaValue);
      const isArrivo = !!this.documento?.arrivo;
      const caveatControl = this.docForm.controls.caveatDiSicurezzaId;
      const caveatAltroControl = this.docForm.controls.caveatDiSicurezzaAltro;
      const caveatValue = this.caveatDiSicurezzaSignal();
      const isCaveatAltro = caveatValue === 4;

      if (isNuova && !documentoAnalogico) {
        modalitaControl.setValidators(Validators.required);
        prodottoSoftwareControl.setValidators(Validators.required);
      } else {
        modalitaControl.clearValidators();
        prodottoSoftwareControl.clearValidators();
      }

      if (isNuova) {
        tipoDocumentoControl.setValidators(Validators.required);
        ambitoUtilizzoControl.setValidators(Validators.required);
        mittenteControl.setValidators(Validators.required);
        if (documentoAnalogico) {
          this.docForm.controls.numeroPagine.setValidators(Validators.required);
        } else {
          this.docForm.controls.numeroPagine.clearValidators();
        }
        if (hasClassificaConVisibilita) {
          this.docForm.controls.organoSicurezzaOriginatore.setValidators(Validators.required);
          this.docForm.controls.organoSicurezzaDestinatario.setValidators(Validators.required);
        } else {
          this.docForm.controls.organoSicurezzaOriginatore.clearValidators();
          this.docForm.controls.organoSicurezzaDestinatario.clearValidators();
        }
        caveatControl.setValidators(Validators.required);
        if (isCaveatAltro) {
          caveatAltroControl.setValidators([Validators.required, Validators.maxLength(255)]);
        } else {
          caveatAltroControl.setValidators(Validators.maxLength(255));
        }
      } else {
        tipoDocumentoControl.clearValidators();
        ambitoUtilizzoControl.clearValidators();
        if (isArrivo) {
          mittenteControl.setValidators(Validators.required);
        } else {
          mittenteControl.clearValidators();
        }
        this.docForm.controls.numeroPagine.clearValidators();
        this.docForm.controls.organoSicurezzaOriginatore.clearValidators();
        this.docForm.controls.organoSicurezzaDestinatario.clearValidators();
        caveatControl.clearValidators();
        caveatAltroControl.setValidators(Validators.maxLength(255));
      }

      modalitaControl.updateValueAndValidity({ emitEvent: false });
      prodottoSoftwareControl.updateValueAndValidity({ emitEvent: false });
      tipoDocumentoControl.updateValueAndValidity({ emitEvent: false });
      ambitoUtilizzoControl.updateValueAndValidity({ emitEvent: false });
      mittenteControl.updateValueAndValidity({ emitEvent: false });
      caveatControl.updateValueAndValidity({ emitEvent: false });
      caveatAltroControl.updateValueAndValidity({ emitEvent: false });
      this.docForm.controls.numeroPagine.updateValueAndValidity({ emitEvent: false });
      this.docForm.controls.organoSicurezzaOriginatore.updateValueAndValidity({ emitEvent: false });
      this.docForm.controls.organoSicurezzaDestinatario.updateValueAndValidity({ emitEvent: false });
      this.docForm.updateValueAndValidity({ emitEvent: false });

      this.changed();
    });
  }

  get ultimoStato(): StoricoStatoDocumento {
    if (this.storicoStati) {
      const stato = this.storicoStati.find(s => s.toStato === this.documento.statoId);
      if (stato) {
        return stato;
      }
    }

    return {} as StoricoStatoDocumento;
  }


  ngOnInit(): void {
    this.anagraficheService.indirizzi.getAll()
      .pipe(takeUntil(this.notifier$))
      .subscribe(indirizzi => {
        this.soggetti = indirizzi
          .filter(indirizzo => !!indirizzo.nomeIndirizzo && indirizzo.bloccato !== true)
          .sort((a, b) => a.nomeIndirizzo.localeCompare(b.nomeIndirizzo, undefined, { sensitivity: 'base' }));
        this.cdRef.markForCheck();
      });

    this.prodottiSoftwareService.getAll()
      .pipe(takeUntil(this.notifier$))
      .subscribe(items => {
        this.prodottiSoftware = items
          .filter(item => item.prodottoSoftwareId != null)
          .map(item => ({
            label: `${item.nomeProdotto} - ${item.versioneProdotto}`,
            value: item.prodottoSoftwareId
          }));
      });

    this.docForm.controls.qualifica.valueChanges.pipe(
      takeUntil(this.notifier$),
      tap(() => {
        if (this.docForm.controls.classifica.value) {
          const classificaData = this.anagraficheService.classifiche.getAsArray().find(e => e.classificaId === this.docForm.controls.classifica.value?.classificaId);
          if (!classificaData) return;

          this.docForm.controls.classifica.setValue(classificaData);
        }
      })
    ).subscribe();

    this.docForm.get('classifica')?.valueChanges.subscribe(value =>
      {
        const scaricaAllegatiControl = this.docForm.get('scaricaAllegati');
        const noCarico = this.docForm.get('noCarico');

        if (scaricaAllegatiControl)
        {
          if (( this.documento.classificaOriginaleId == 3 ||this.documento.classificaOriginaleId == 4)  && (value?.classificaId === 2 || value?.classificaId === 1|| value?.classificaId === 6 || noCarico?.value === true))
          {
                scaricaAllegatiControl.setValue(true);
          }
          else
          {
            scaricaAllegatiControl.setValue(false);
          }
        }
      }
    );

    this.docForm.get('noCarico')?.valueChanges.subscribe(value =>
      {
        {
          const scaricaAllegatiControl = this.docForm.get('scaricaAllegati');
          if (scaricaAllegatiControl)
          {
           // scaricaAllegatiControl.setValue(0);
                 // setTimeout(() => scaricaAllegatiControl.setValue(value));
                 scaricaAllegatiControl.setValue(value);
                //  if (value)
                //  scaricaAllegatiControl.setValue(true);
                // else
                // setTimeout(() => scaricaAllegatiControl.setValue(false));
                //  // scaricaAllegatiControl.setValue(false);
          }
        }
      }
    );

    this.docForm.controls.dataDocumento.valueChanges.pipe(
      takeUntil(this.notifier$)
    ).subscribe(value => {
      this.dataDocumentoSignal.set(value);
    });
  }

  ngOnDestroy(): void {
    if (this.docFormChangesSubscription) {
      this.docFormChangesSubscription.unsubscribe();
    }
    this.notifier$.next();
    this.notifier$.complete();
  }

  filterQualifica(list: Qualifica[]): Qualifica[] {
    if (this.newDocument !== undefined && !this.newDocument) return list;
    return list.filter(e => e.nomeQualifica !== 'UEO');
  }

  writeValue(value: Documento): void {
    if (value) {
      this.documento = value;
      this.newDocument = !this.documento.documentoId;
      this.storicoStati = this.documento.storicoStatiDocumento?.reverse();

      if (!this.newDocument && !this.trasmissioneRiproduzione) {
        this.docForm.controls.classifica.disable();
        this.docForm.controls.qualifica.disable();
        this.docForm.controls.esercitazione.disable();
        this.docForm.controls.noCarico.disable();
        this.docForm.controls.scaricaAllegati.disable();
      }

      if (this.documento.arrivo) {
        const isNuova = this.documentoNormativaService.isUnderNewNormativa(this.documento.dataDocumento);
        if (isNuova) {
          this.docForm.controls.mittente.clearValidators();
        } else {
          this.docForm.controls.mittente.setValidators(Validators.required);
        }
        this.docForm.controls.protocolloMittente.setValidators(Validators.required);
        this.docForm.controls.destinatari.clearValidators();
        this.docForm.controls.dataInvio.clearValidators();
      } else {
        this.docForm.controls.mittente.clearValidators();
        this.docForm.controls.protocolloMittente.clearValidators();
        this.docForm.controls.dataInvio.setValidators(Validators.required);
      }

      this.setFormValues();
    } else {
      this.documento = {} as Documento;
      this.storicoStati = [];
    }
  }

  registerOnChange(fn: (_: Documento) => void): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouchedFn = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.edit = !isDisabled;

    if (this.edit) {
      this.setFormValues();
    }
  }

  validate(): ValidationErrors | null {
    if (this.docForm.valid) {
      return null;
    }

    return { required: true };
  }

  changed(): void {
    const ctrls = this.docForm.controls;
    const docChanged = {
      ...this.documento,
      oggetto: ctrls.oggetto.value,
      dataDocumento: ctrls.dataDocumento.value,
      protocolloMittente: ctrls.protocolloMittente.value,
      numeroControllo: ctrls.numeroControllo.value,
      dataInvio: ctrls.dataInvio.value,
      note: ctrls.note.value,
      lingua: ctrls.lingua.value,
      paroleChiave: ctrls.paroleChiave.value ?? [],
      documentoAnalogico: ctrls.documentoAnalogico.value,
      numeroPagine: ctrls.numeroPagine.value,
      organoSicurezzaOriginatore: ctrls.organoSicurezzaOriginatore.value,
      organoSicurezzaDestinatario: ctrls.organoSicurezzaDestinatario.value,
      caveatDiSicurezzaId: ctrls.caveatDiSicurezzaId.value,
      caveatDiSicurezzaAltro: ctrls.caveatDiSicurezzaAltro.value,
      prodottoSoftwareId: ctrls.prodottoSoftwareId.value,
      modalitaFormazioneId: ctrls.modalitaFormazioneId.value,
      tipoDocumentoId: ctrls.tipoDocumentoId.value,
      ambitoUtilizzoId: ctrls.ambitoUtilizzoId.value,
      classificaId: ctrls.classifica.value?.classificaId,
      qualificaId: ctrls.qualifica.value?.qualificaId,
      ambito: ctrls.ambito.value,
      sezioneId: ctrls.sezioneId.value,
      esercitazione: ctrls.esercitazione.value,
      mittenteId: ctrls.mittente.value?.indirizzoId,
      precedenzaId: ctrls.precedenzaId.value,
      documentoDestinatario: ctrls.destinatari.value?.map(d => ({ documentoId: this.documento.documentoId, destinatarioId: d.indirizzoId } as DocumentoDestinatario)),
      copieInCarico: ctrls.caricoArrivo.controls.copieInCarico.value,
      noCarico: ctrls.noCarico.value,
      numeroEsemplariRicevuti: ctrls.caricoArrivo.controls.numeroEsemplariRicevuti.value,
      numeroDistintoCopie: ctrls.caricoArrivo.controls.numeroDistintoCopie.value,
      numeroAllegati: this.documento.arrivo ? ctrls.caricoArrivo.controls.allegatiRicevuti.value : ctrls.caricoPartenza.controls.allegatiOriginati.value,
      noteEtichetta: ctrls.noteEtichetta.value,
      scaricaAllegati: ctrls.scaricaAllegati.value
    } as Documento;


    if (this.onChangeFn) {
      this.onChangeFn(docChanged);
    }

    if (this.onTouchedFn) {
      this.onTouchedFn();
    }
  }

  addIndirizzo(name: string | null): void {
    if (name) {
      const ind = {
        isConfirmed: false,
        nomeIndirizzo: name
      } as Indirizzo;

      this.anagraficheService.indirizzi.create(ind).subscribe();
    }
  }

  openSoggettoDialog(): void {
    this.soggettoDialog?.open();
  }

  getToolip(controlName: string): string {
    const errors = (this.docForm.controls[controlName] as FormControl)?.errors;
    if (errors?.mustCreate === true) {
      return 'Apri la modale con il tasto + e crea il nuovo soggetto';
    }

    if (errors?.required === true) {
      return 'Inserimento obbligatorio';
    }

    if (errors?.maxLength === true) {
      return 'Massimo 255 caratteri per singola parola chiave';
    }

    return '';
  }

  public classificaChanged(): void {
    const classificaId = this.docForm.controls.classifica.value?.classificaId;
    const isSegreto = classificaId === 3 || classificaId === 4;

    if (isSegreto) {
      this.docForm.controls.lingua.setValidators(Validators.required);
      if (this.documento.arrivo) {
        this.docForm.controls.numeroControllo.setValidators(Validators.required);
        this.docForm.controls.caricoArrivo?.setValidators(Validators.required);
        this.docForm.controls.caricoArrivo?.enable();
        this.docForm.controls.caricoPartenza?.clearValidators();
        this.docForm.controls.caricoPartenza?.disable();
      } else {
        this.docForm.controls.caricoPartenza?.setValidators(Validators.required);
        this.docForm.controls.caricoPartenza?.enable();
        this.docForm.controls.caricoArrivo?.clearValidators();
        this.docForm.controls.caricoArrivo?.disable();
      }
    } else {
      this.docForm.controls.lingua.clearValidators();
      this.docForm.controls.numeroControllo.clearValidators();
      this.docForm.controls.caricoArrivo?.clearValidators();
      this.docForm.controls.caricoArrivo?.disable();
      this.docForm.controls.caricoPartenza?.clearValidators();
      this.docForm.controls.caricoPartenza?.disable();
    }

    this.docForm.controls.lingua.updateValueAndValidity();
    this.docForm.controls.numeroControllo.updateValueAndValidity();
    this.docForm.controls.lingua.setValue(this.docForm.controls.lingua.value);
    this.setSezioneId();
    this.cdRef.detectChanges();
  }

  public getSezione(): string {
    const sezioneId = this.docForm.controls.sezioneId.value;
    if (!sezioneId) {
      return '';
    }

    const sezione = this.anagraficheService.sezioni.getAsArray().find(s => s.sezioneId === sezioneId);
    return `${sezione?.nomeSezione ?? ''} - ${sezione?.descrizione ?? ''}`;
  }

  public setSezioneId(): void {
    const docForm = this.docForm.value;
    if (!docForm.classifica && !docForm.qualifica && !docForm.esercitazione) {
      return;
    }

    let sezione: Sezione | undefined;
    if (docForm.esercitazione) {
      sezione = this.anagraficheService.sezioni.getAsArray().find(s => s.nomeSezione === 'E');
    } else {
      sezione = this.anagraficheService.sezioni.getAsArray().find(s => s.classificaId === docForm.classifica?.classificaId && s.qualificaId === docForm.qualifica?.qualificaId);
    }

    this.docForm.controls.sezioneId.setValue(sezione?.sezioneId ?? null);
  }

  public isSegreto(): boolean {
    return this.docForm.controls.classifica.value?.classificaId === 3 || this.docForm.controls.classifica.value?.classificaId === 4;
  }
  public isOriginaleSegreto(): boolean {
    return (this.documento && (this.documento.classificaOriginaleId === 3 || this.documento.classificaOriginaleId === 4));
  }
  private setFormValues() {
    const qualifica = this.anagraficheService.qualifiche.getAsArray().find(q => q.qualificaId === this.documento.qualificaId);
    const classifica = this.anagraficheService.classifiche.getAsArray().find(q => q.classificaId === this.documento.classificaId);
    const mittente = this.anagraficheService.indirizzi.getAsArray().find(i => i.indirizzoId === this.documento.mittenteId);
    let destinatari: Indirizzo[] = [];
    if (this.destinatariArray.length > 0) {
      destinatari = this.destinatariArray;
    }
    else {
      destinatari = this.anagraficheService.indirizzi.getAsArray().filter(i => this.documento.documentoDestinatario?.some(dd => dd.destinatarioId === i.indirizzoId));
    }

    this.docForm.patchValue({
      oggetto: this.documento.oggetto ?? null,
      dataDocumento: this.documento.dataDocumento ?? null,
      protocolloMittente: this.documento.protocolloMittente ?? null,
      numeroControllo: this.documento.numeroControllo ?? null,
      dataInvio: this.documento.dataInvio ?? null,
      note: this.documento.note ?? null,
      paroleChiave: this.documento.paroleChiave ?? [],
      lingua: this.documento.lingua ?? 'Italiano',
      modalitaFormazioneId: this.documento.modalitaFormazioneId ?? null,
      tipoDocumentoId: this.documento.tipoDocumentoId ?? null,
      ambitoUtilizzoId: this.documento.ambitoUtilizzoId ?? null,
      documentoAnalogico: this.documento.documentoAnalogico ?? false,
      prodottoSoftwareId: this.documento.prodottoSoftwareId ?? null,
      numeroPagine: this.documento.numeroPagine ?? null,
      organoSicurezzaOriginatore: this.documento.organoSicurezzaOriginatore ?? null,
      organoSicurezzaDestinatario: this.documento.organoSicurezzaDestinatario ?? null,
      caveatDiSicurezzaId: this.documento.caveatDiSicurezzaId ?? null,
      caveatDiSicurezzaAltro: this.documento.caveatDiSicurezzaAltro ?? null,
      qualifica: qualifica,
      sezioneId: this.documento.sezioneId,
      esercitazione: this.documento.esercitazione,
      mittente: mittente,
      precedenzaId: this.documento.precedenzaId,
      ambito: this.documento.ambito ?? null,
      destinatari: destinatari,
      noCarico: this.documento.noCarico,
      caricoArrivo: {
        copieInCarico: this.documento.copieInCarico,
        numeroEsemplariRicevuti: this.documento.numeroEsemplariRicevuti,
        numeroDistintoCopie: this.documento.numeroDistintoCopie,
        allegatiRicevuti: this.documento.numeroAllegati,
      },
      caricoPartenza: {
        allegatiOriginati: this.documento.numeroAllegati
      },
      noteEtichetta: this.documento.noteEtichetta ?? null,
      scaricaAllegati: this.documento.scaricaAllegati
      // ,
      // numeroRichiesta: this.documentoRiproduzione.numeroRichiesta,
      // richiedente: this.documentoRiproduzione.richiedente,
      // dataRichiesta: this.documentoRiproduzione.dataRichiesta
    });

    this.dataDocumentoSignal.set(this.documento.dataDocumento ?? null);

    setTimeout(() => this.docForm.patchValue({ classifica: classifica }));
  }

  public shouldShowOrganiSicurezza(): boolean {
    return this.hasClassificaConVisibilita();
  }

  private hasClassificaConVisibilita(): boolean {
    const classifica = this.docForm.controls.classifica.value ?? null;
    return !!classifica && !this.isClassificaNonClassificata(classifica);
  }

  private isClassificaNonClassificata(classifica: Classifica | null | undefined): boolean {
    if (!classifica) return true;
    return classifica.nomeClassifica?.toLowerCase().trim() === 'non classificato';
  }

  public useSections(): boolean {
    return this.appConfig.logic.useSections;
  }

  setToday() {
    this.docForm.controls.dataDocumento.setValue(new Date());
  }

  trackByPosizioneId(index: number, posizione: Posizione): string {
    return posizione.posizioneId;
  }

  getModalitaFormazioneLabel(id: number | null | undefined): string {
    if (!id) return '';
    return this.modalitaFormazione.find(m => m.value === id)?.label || '';
  }

  getTipoDocumentoLabel(id: number | null | undefined): string {
    if (!id) return '';
    return this.tipiDocumento.find(t => t.value === id)?.label || '';
  }

  getAmbitoUtilizzoLabel(id: number | null | undefined): string {
    if (!id) return '';
    return this.ambitiUtilizzo.find(a => a.value === id)?.label || '';
  }

  getProdottoSoftwareLabel(id: number | null | undefined): string {
    if (!id) return '';
    return this.prodottiSoftware.find(p => p.value === id)?.label || '';
  }

  getCaveatDiSicurezzaLabel(id: number | null | undefined): string {
    if (!id) return '';
    return this.caveatDiSicurezza.find(c => c.value === id)?.label || '';
  }

  getCaveatDiSicurezzaDisplay(): string {
    const caveatId = this.docForm.controls.caveatDiSicurezzaId.value ?? this.documento.caveatDiSicurezzaId ?? null;
    if (!caveatId) return '';
    const label = this.getCaveatDiSicurezzaLabel(caveatId);
    if (caveatId === 4) {
      const altro = this.docForm.controls.caveatDiSicurezzaAltro.value ?? this.documento.caveatDiSicurezzaAltro;
      return altro ? `${label}: ${altro}` : label;
    }
    return label;
  }

}
